import {
  getPossibleVariables,
  type Block,
  type OgImageBlock,
} from '@basehub/data/replicache/index'
import * as PropertiesPanel from '../../properties-panel'
import {
  getOgBlockStore,
  processRotationInput,
  useLayerFunctions,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/utils'
import {
  GoogleFontsItem,
  GoogleFontsListResponse,
  OgImageBlockLayerType,
  OgImageBlockShapeLayerType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/types'
import * as React from 'react'
import { Button } from '~/sotano-kit/primitives/button'
import {
  CornerTopLeftIcon,
  ImageIcon,
  OpacityIcon,
  ReloadIcon,
  TrashIcon,
} from '@radix-ui/react-icons'
import {
  CustomTextOption,
  FontComboboxInput,
  FontWeightSelectInput,
  HorizontalColorInput,
  ImageFitSelectInput,
  ShortNumberInput,
  StaticImageInput,
  TextHorizontalAlignSelector,
  TextVerticalAlignSelector,
  basicShortNumberInputRegex,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/custom-inputs'
import { sidebarLocalStorageKeys } from '~/lib/dom-keys'
import { useStorage } from '~/hooks/use-storage'
import { useEditorMode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-mode-provider'
import { env } from '~/env.js'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import {
  GetVariables,
  Variable,
} from '~/components/variables/single-line-input'
import { getOgBlockRootOptions } from './vars'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const OgImageBlockPropertiesPanel = ({
  block: _block,
  parent,
  path,
}: {
  block: OgImageBlock
  parent: Block
  path: string
}) => {
  const editorMode = useEditorMode()
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const root = useWorkingTreeStore((s) => s.tree?.root)
  const repo = useSelectedTeamAndRepo().data?.repo
  const sourceBlockId = idToSourceBlockId(_block)
  const selectedVariants = useSelectedVariants({
    parentBlock: parent,
    ignoreDefault: false,
  })
  const parentNode = useTreeSlice(treeManager, (s) => {
    return s.getNode(parent.id)
  })

  const getVariables: GetVariables = React.useCallback(
    (segments) => {
      if (!root || !getChildrenKeys || !getNode || !repo) return []
      const nodeBlock = parentNode
      if (!nodeBlock) return []
      try {
        return getPossibleVariables({
          segments,
          nodeBlock,
          treeManager: { getChildrenKeys, getNode },
          valueKey: 'value',
          rootOptions: getOgBlockRootOptions({
            blockTreeManager: { root },
            repo,
          }),
          selectedVariants,
        })
          .filter((v) => !v.hidden)
          .map((v) => {
            return {
              info: v.value,
              label: v.name,
              displayLabel: v.displayLabel,
            } satisfies Variable
          })
      } catch (error) {
        console.error(error)
        return []
      }
    },
    [root, getChildrenKeys, getNode, repo, parentNode, selectedVariants]
  )

  const {
    selectedLayerPositionZ,
    floatingLabelTwitter,
    setFloatingLabelTwitter,
  } = getOgBlockStore(_block.id)((state) => ({
    selectedLayerPositionZ: state.selectedLayerPositionZ,
    floatingLabelTwitter: state.floatingLabelTwitter,
    setFloatingLabelTwitter: state.setFloatingLabelTwitter,
  }))

  const { updateLayer, removeLayer, value } = useLayerFunctions({
    block: _block,
    path,
    shouldSync: () => {
      if (
        document.activeElement instanceof HTMLElement &&
        (document.activeElement.classList.contains('cm-content') ||
          document.activeElement.closest('.cm-content'))
      ) {
        return false
      }
      return true
    },
    parent,
  })

  const backgroundLayer = value.layersData.find(
    (layer) => layer.positionZ === 0
  ) as OgImageBlockShapeLayerType

  // Sidebar width
  const [sidebarWidth] = useStorage(
    'localStorage',
    (z) => z.number(),
    sidebarLocalStorageKeys.documentSidebarWidth
  )

  // Load available fonts
  const [availableFonts, setAvailableFonts] = React.useState<GoogleFontsItem[]>(
    []
  )

  React.useEffect(() => {
    const getAvailableFonts = async () => {
      const res = await fetch(
        `https://www.googleapis.com/webfonts/v1/webfonts?key=${env.NEXT_PUBLIC_GOOGLE_FONTS_API_KEY}&subset=latin&sort=popularity`
      )
      const googleFonts = (await res.json()) as GoogleFontsListResponse

      setAvailableFonts(
        googleFonts.items.sort((a, b) => a.family.localeCompare(b.family))
      )
    }
    getAvailableFonts()
  }, [])

  return (
    <PropertiesPanel.PropertiesContainer>
      {!value.showTemplates &&
        editorMode === 'write:all' &&
        value.layersData &&
        value.layersData.filter(
          (layer) =>
            layer.positionZ.toString() === selectedLayerPositionZ?.toString()
        ).length === 0 && (
          <>
            <PropertiesPanel.Group title="Background" spacing="tight">
              <HorizontalColorInput
                label="Color"
                value={backgroundLayer.color}
                mutate={async (newValue) => {
                  const formatValue = newValue.startsWith('#')
                    ? newValue
                    : `#${newValue}`
                  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
                  const formatColor = formatValue.match(hexRegex)
                    ? formatValue
                    : backgroundLayer.color
                  await updateLayer({
                    ...backgroundLayer,
                    color: formatColor,
                  })
                  return formatColor
                }}
                layerId={`${sourceBlockId}-layer-bg`}
              />
            </PropertiesPanel.Group>
            <PropertiesPanel.Group title="Visual Guides" spacing="tight">
              <PropertiesPanel.BooleanOption
                name="Twitter/X floating label"
                id={`el-${sourceBlockId}-pp-floating-label-twitter`}
                value={floatingLabelTwitter}
                mutate={setFloatingLabelTwitter}
              />
            </PropertiesPanel.Group>
          </>
        )}
      {value.layersData &&
        value.layersData.map((selectedLayer: OgImageBlockLayerType, i) => {
          const isSelected =
            selectedLayer.positionZ.toString() ===
            selectedLayerPositionZ?.toString()
          if (!isSelected) return null

          return (
            <React.Fragment key={i}>
              <PropertiesPanel.Group title="Selected layer" spacing="tight">
                {selectedLayer.type === 'text' && (
                  <>
                    <div className="flex gap-1.5 flex-col w-full justify-center items-center">
                      <CustomTextOption
                        name="Value"
                        multiLine
                        forceEditable
                        className="w-full"
                        value={selectedLayer.textValue}
                        mutate={async (v) => {
                          await updateLayer({
                            ...selectedLayer,
                            textValue: v,
                          })
                        }}
                        getVariables={getVariables}
                      />

                      <p className="w-full cursor-default text-xs text-dark-faint inline-flex justify-start items-center gap-1">
                        Variables supported (Ctrl+Space)
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 !mt-4 !mb-2">
                      <HorizontalColorInput
                        layerId={`${sourceBlockId}-layer-${i}`}
                        label="Color"
                        value={selectedLayer.color}
                        mutate={async (newValue) => {
                          const formatValue = newValue.startsWith('#')
                            ? newValue
                            : `#${newValue}`

                          const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
                          const formatColor = formatValue.match(hexRegex)
                            ? formatValue
                            : selectedLayer.color

                          await updateLayer({
                            ...selectedLayer,
                            color: formatColor,
                          })
                          return formatColor
                        }}
                      />
                      <ShortNumberInput
                        name="clamp"
                        label="Line Clamp"
                        value={selectedLayer.lineClamp.toString()}
                        mutate={async (newValue) => {
                          const formatValue = Number(
                            newValue
                              .replace(',', '.')
                              .replace(basicShortNumberInputRegex, '')
                          )

                          const minClamp = 1

                          if (
                            (formatValue && formatValue < minClamp) ||
                            (formatValue === 0 && newValue === '0')
                          ) {
                            await updateLayer({
                              ...selectedLayer,
                              lineClamp: 1,
                            })
                            return '1'
                          } else {
                            const newClamp = formatValue
                              ? Math.round(formatValue)
                              : selectedLayer.lineClamp

                            await updateLayer({
                              ...selectedLayer,
                              lineClamp: newClamp,
                            })
                            return newClamp.toString()
                          }
                        }}
                      />
                    </div>
                  </>
                )}
                {selectedLayer.type === 'image' && (
                  <>
                    <div className="flex gap-1.5 flex-col w-full justify-center items-center mb-2">
                      <CustomTextOption
                        name="Image URL"
                        forceEditable
                        className="w-full"
                        value={
                          selectedLayer.imageValue.type === 'dynamic'
                            ? selectedLayer.imageValue.value
                            : ''
                        }
                        mutate={async (newValue) => {
                          await updateLayer({
                            ...selectedLayer,
                            imageValue: {
                              type: 'dynamic',
                              value: newValue,
                            },
                          })
                        }}
                        getVariables={getVariables}
                      />

                      <p className="w-full cursor-default text-xs text-dark-faint inline-flex justify-start items-center gap-1">
                        Variables supported (Ctrl+Space)
                      </p>
                    </div>

                    <StaticImageInput
                      layer={selectedLayer}
                      updateLayer={updateLayer}
                    />
                  </>
                )}
                {selectedLayer.type === 'shape' && (
                  <HorizontalColorInput
                    layerId={`${sourceBlockId}-layer-${i}`}
                    label="Color"
                    value={selectedLayer.color}
                    mutate={async (newValue) => {
                      const formatValue = newValue.startsWith('#')
                        ? newValue
                        : `#${newValue}`

                      const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
                      const formatColor = formatValue.match(hexRegex)
                        ? formatValue
                        : selectedLayer.color

                      await updateLayer({
                        ...selectedLayer,
                        color: formatColor,
                      })
                      return formatColor
                    }}
                  />
                )}
                <div className="grid grid-cols-2 gap-4 !mt-4">
                  <ShortNumberInput
                    name="X"
                    label="X"
                    horizontal
                    value={selectedLayer.positionX.toString()}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const minX = 0
                      const maxX = 1200 - selectedLayer.width

                      if (
                        (formatValue && formatValue < minX) ||
                        (formatValue === 0 && newValue === '0')
                      ) {
                        await updateLayer({
                          ...selectedLayer,
                          positionX: 0,
                        })
                        return '0'
                      } else if (formatValue && formatValue > maxX) {
                        await updateLayer({
                          ...selectedLayer,
                          positionX: maxX,
                        })
                        return maxX.toString()
                      } else {
                        const newX = formatValue
                          ? Math.round(formatValue)
                          : selectedLayer.positionX

                        await updateLayer({
                          ...selectedLayer,
                          positionX: newX,
                        })
                        return newX.toString()
                      }
                    }}
                  />
                  <ShortNumberInput
                    name="Y"
                    label="Y"
                    horizontal
                    value={selectedLayer.positionY.toString()}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const minY = 0
                      const maxY = 630 - selectedLayer.height

                      if (
                        (formatValue && formatValue < minY) ||
                        (formatValue === 0 && newValue === '0')
                      ) {
                        await updateLayer({
                          ...selectedLayer,
                          positionY: 0,
                        })
                        return '0'
                      } else if (formatValue && formatValue > maxY) {
                        await updateLayer({
                          ...selectedLayer,
                          positionY: maxY,
                        })
                        return maxY.toString()
                      } else {
                        const newY = formatValue
                          ? Math.round(formatValue)
                          : selectedLayer.positionY

                        await updateLayer({
                          ...selectedLayer,
                          positionY: newY,
                        })
                        return newY.toString()
                      }
                    }}
                  />
                  <ShortNumberInput
                    name="W"
                    label="W"
                    horizontal
                    value={selectedLayer.width.toString()}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const minWidth = 1
                      const maxWidth = 1200 - selectedLayer.positionX

                      if (formatValue && formatValue < minWidth) {
                        await updateLayer({
                          ...selectedLayer,
                          width: selectedLayer.width,
                        })
                        return selectedLayer.width.toString()
                      } else if (formatValue && formatValue > maxWidth) {
                        await updateLayer({
                          ...selectedLayer,
                          width: maxWidth,
                        })
                        return maxWidth.toString()
                      } else {
                        const newWidth = formatValue
                          ? Math.round(formatValue)
                          : selectedLayer.width

                        await updateLayer({
                          ...selectedLayer,
                          width: newWidth,
                        })
                        return newWidth.toString()
                      }
                    }}
                  />
                  <ShortNumberInput
                    name="H"
                    label="H"
                    horizontal
                    value={selectedLayer.height.toString()}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const minHeight = 1
                      const maxHeight = 630 - selectedLayer.positionY

                      if (formatValue && formatValue < minHeight) {
                        await updateLayer({
                          ...selectedLayer,
                          height: selectedLayer.height,
                        })
                        return selectedLayer.height.toString()
                      } else if (formatValue && formatValue > maxHeight) {
                        await updateLayer({
                          ...selectedLayer,
                          height: maxHeight,
                        })
                        return maxHeight.toString()
                      } else {
                        const newHeight = formatValue
                          ? Math.round(formatValue)
                          : selectedLayer.height

                        await updateLayer({
                          ...selectedLayer,
                          height: newHeight,
                        })
                        return newHeight.toString()
                      }
                    }}
                  />
                  <ShortNumberInput
                    name="rotation"
                    label={<ReloadIcon width={14} height={14} />}
                    horizontal
                    value={selectedLayer.rotation.toString() + '°'}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const formatRotation =
                        formatValue || formatValue === 0
                          ? Math.round(formatValue)
                          : selectedLayer.rotation

                      await updateLayer({
                        ...selectedLayer,
                        rotation: processRotationInput(formatRotation),
                      })
                      return formatRotation.toString() + '°'
                    }}
                  />
                  <ShortNumberInput
                    name="opacity"
                    label={<OpacityIcon width={16} height={16} />}
                    horizontal
                    value={(selectedLayer.opacity * 100).toString() + '%'}
                    mutate={async (newValue) => {
                      const formatValue = Number(
                        newValue
                          .replace(',', '.')
                          .replace(basicShortNumberInputRegex, '')
                      )

                      const minOpacity = 0
                      const maxOpacity = 100

                      if (
                        (formatValue && formatValue < minOpacity) ||
                        (formatValue === 0 && newValue === '0')
                      ) {
                        await updateLayer({
                          ...selectedLayer,
                          opacity: 0,
                        })
                        return '0%'
                      } else if (formatValue && formatValue > maxOpacity) {
                        await updateLayer({
                          ...selectedLayer,
                          opacity: 1,
                        })
                        return '100%'
                      } else {
                        const formatOpacity =
                          formatValue || formatValue === 0
                            ? Math.round(formatValue)
                            : selectedLayer.opacity

                        await updateLayer({
                          ...selectedLayer,
                          opacity: formatOpacity / 100,
                        })
                        return formatOpacity.toString() + '%'
                      }
                    }}
                  />
                  {(selectedLayer.type === 'shape' ||
                    selectedLayer.type === 'image') && (
                    <ShortNumberInput
                      name="cornerRadius"
                      label={<CornerTopLeftIcon width={16} height={16} />}
                      horizontal
                      value={(selectedLayer.cornerRadius || 0).toString()}
                      mutate={async (newValue) => {
                        const formatValue = Number(
                          newValue
                            .replace(',', '.')
                            .replace(basicShortNumberInputRegex, '')
                        )

                        const formatCornerRadius =
                          formatValue || formatValue === 0
                            ? Math.round(formatValue)
                            : selectedLayer.cornerRadius

                        await updateLayer({
                          ...selectedLayer,
                          cornerRadius: formatCornerRadius,
                        })
                        return formatCornerRadius.toString()
                      }}
                    />
                  )}
                  {selectedLayer.type === 'image' && (
                    <ImageFitSelectInput
                      label={<ImageIcon width={15} height={15} />}
                      value={selectedLayer.objectFit}
                      mutate={async (newWeightValue) => {
                        await updateLayer({
                          ...selectedLayer,
                          objectFit: newWeightValue as
                            | 'contain'
                            | 'cover'
                            | 'fill',
                        })
                      }}
                    />
                  )}
                </div>
                {selectedLayer.type === 'text' && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-4 !mt-6">
                    <FontComboboxInput
                      label="Font"
                      value={selectedLayer.fontFamily}
                      mutate={async (newValue) => {
                        const newSelectedFont = availableFonts?.find(
                          (font) => font.family.toLowerCase() === newValue
                        )

                        if (!newSelectedFont) return

                        await updateLayer({
                          ...selectedLayer,
                          fontFamily: newSelectedFont.family,
                          fontUrl:
                            newSelectedFont.files.regular ??
                            newSelectedFont.files['400'] ??
                            '', // Some fonts use regular, some use 400
                          fontWeight: 400,
                        })
                      }}
                      availableFonts={availableFonts}
                      popoverContentWidth={
                        sidebarWidth ? sidebarWidth - 40 : undefined
                      }
                    />
                    <FontWeightSelectInput
                      label="Weight"
                      value={selectedLayer.fontWeight}
                      fontFamily={selectedLayer.fontFamily}
                      availableFonts={availableFonts}
                      mutate={async (newWeightValue) => {
                        const newFontUrl = availableFonts?.find(
                          (font) => font?.family === selectedLayer.fontFamily
                        )?.files[
                          newWeightValue === '400' ? 'regular' : newWeightValue
                        ]

                        if (!newFontUrl) return

                        await updateLayer({
                          ...selectedLayer,
                          fontWeight: Number(newWeightValue),
                          fontUrl: newFontUrl,
                        })
                      }}
                    />
                    <ShortNumberInput
                      name="size"
                      label="Size"
                      value={selectedLayer.size.toString()}
                      mutate={async (newValue) => {
                        const formatValue = Number(
                          newValue
                            .replace(',', '.')
                            .replace(basicShortNumberInputRegex, '')
                        )

                        const formatSize =
                          formatValue && formatValue >= 1
                            ? Math.round(formatValue)
                            : selectedLayer.size

                        await updateLayer({
                          ...selectedLayer,
                          size: formatSize,
                          lineHeight: Math.round(formatSize * 1.2),
                        })
                        return formatSize.toString()
                      }}
                    />
                    <ShortNumberInput
                      name="lineHeight"
                      label="Line height"
                      value={selectedLayer.lineHeight.toString()}
                      mutate={async (newValue) => {
                        const formatValue = Number(
                          newValue
                            .replace(',', '.')
                            .replace(basicShortNumberInputRegex, '')
                        )

                        const formatLineHeight =
                          formatValue && formatValue >= 1
                            ? Math.round(formatValue)
                            : selectedLayer.lineHeight

                        await updateLayer({
                          ...selectedLayer,
                          lineHeight: formatLineHeight,
                        })
                        return formatLineHeight.toString()
                      }}
                    />
                    <TextHorizontalAlignSelector
                      value={selectedLayer.alignHorizontal}
                      mutate={async (newValue) => {
                        await updateLayer({
                          ...selectedLayer,
                          alignHorizontal: newValue as
                            | 'left'
                            | 'center'
                            | 'right'
                            | 'justify',
                        })
                      }}
                    />
                    <TextVerticalAlignSelector
                      value={selectedLayer.alignVertical}
                      mutate={async (newValue) => {
                        await updateLayer({
                          ...selectedLayer,
                          alignVertical: newValue as
                            | 'top'
                            | 'middle'
                            | 'bottom',
                        })
                      }}
                    />
                  </div>
                )}
              </PropertiesPanel.Group>
              <PropertiesPanel.Group>
                <Button
                  type="button"
                  intent="secondary"
                  className="text-xs text-dark-error"
                  onClick={async () => {
                    await removeLayer(selectedLayer)
                  }}
                >
                  <TrashIcon width={16} height={16} className="mr-1" />
                  Remove layer
                </Button>
              </PropertiesPanel.Group>
            </React.Fragment>
          )
        })}
    </PropertiesPanel.PropertiesContainer>
  )
}
